import React, { useState } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon, CalendarDaysIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

// Schedule templates based on business type
const scheduleTemplates = {
  ice_rink: [
    {
      name: 'Public Skate Schedule',
      serviceId: 'public_skate',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 5, startTime: '19:00', endTime: '21:00' }, // Friday evening
        { dayOfWeek: 6, startTime: '14:00', endTime: '16:00' }, // Saturday afternoon
        { dayOfWeek: 6, startTime: '19:00', endTime: '21:00' }, // Saturday evening
        { dayOfWeek: 0, startTime: '14:00', endTime: '16:00' }  // Sunday afternoon
      ]
    },
    {
      name: 'Learn to Skate Classes',
      serviceId: 'learn_to_skate',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 6, startTime: '09:00', endTime: '09:30' }, // Saturday morning - Tots
        { dayOfWeek: 6, startTime: '09:45', endTime: '10:15' }, // Saturday morning - Beginners
        { dayOfWeek: 6, startTime: '10:30', endTime: '11:00' }, // Saturday morning - Intermediate
        { dayOfWeek: 3, startTime: '16:00', endTime: '16:30' }  // Wednesday after school
      ]
    },
    {
      name: 'Adult Hockey League',
      serviceId: 'hockey_league',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 1, startTime: '20:00', endTime: '22:00' }, // Monday night
        { dayOfWeek: 3, startTime: '20:00', endTime: '22:00' }, // Wednesday night
        { dayOfWeek: 0, startTime: '18:00', endTime: '20:00' }  // Sunday evening
      ]
    }
  ],
  training_center: [
    {
      name: 'Morning Bootcamp',
      serviceId: 'bootcamp',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 1, startTime: '06:00', endTime: '07:00' },
        { dayOfWeek: 3, startTime: '06:00', endTime: '07:00' },
        { dayOfWeek: 5, startTime: '06:00', endTime: '07:00' }
      ]
    },
    {
      name: 'Yoga Flow',
      serviceId: 'yoga',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 2, startTime: '18:00', endTime: '19:00' },
        { dayOfWeek: 4, startTime: '18:00', endTime: '19:00' },
        { dayOfWeek: 6, startTime: '10:00', endTime: '11:00' }
      ]
    },
    {
      name: 'Strength Training',
      serviceId: 'strength',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 1, startTime: '17:00', endTime: '18:00' },
        { dayOfWeek: 3, startTime: '17:00', endTime: '18:00' },
        { dayOfWeek: 5, startTime: '17:00', endTime: '18:00' }
      ]
    }
  ],
  sports_complex: [
    {
      name: 'Soccer Drop-In',
      serviceId: 'soccer_dropin',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 2, startTime: '19:00', endTime: '21:00' },
        { dayOfWeek: 4, startTime: '19:00', endTime: '21:00' },
        { dayOfWeek: 0, startTime: '16:00', endTime: '18:00' }
      ]
    },
    {
      name: 'Basketball Pickup',
      serviceId: 'basketball_pickup',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 1, startTime: '18:00', endTime: '20:00' },
        { dayOfWeek: 3, startTime: '18:00', endTime: '20:00' },
        { dayOfWeek: 6, startTime: '14:00', endTime: '16:00' }
      ]
    }
  ],
  dance_martial_arts: [
    {
      name: 'Kids Dance',
      serviceId: 'kids_dance',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 2, startTime: '16:00', endTime: '17:00' },
        { dayOfWeek: 4, startTime: '16:00', endTime: '17:00' }
      ]
    },
    {
      name: 'Adult Martial Arts',
      serviceId: 'adult_martial_arts',
      recurrence: 'weekly',
      instances: [
        { dayOfWeek: 1, startTime: '19:00', endTime: '20:30' },
        { dayOfWeek: 3, startTime: '19:00', endTime: '20:30' },
        { dayOfWeek: 5, startTime: '19:00', endTime: '20:30' }
      ]
    }
  ],
  other: []
}

const daysOfWeek = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
]

const recurrenceOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' }
]

export default function ScheduleTemplatesStep() {
  const { wizardData, updateStepData, getStepErrors } = useSetupWizard()
  const colors = useThemeColors()
  const { scheduleTemplates: schedules, services, organizationProfile } = wizardData
  const errors = getStepErrors(7)
  const [expandedSchedule, setExpandedSchedule] = useState<string | null>(null)

  const addScheduleTemplate = () => {
    const newSchedule = {
      id: `schedule-${Date.now()}`,
      name: '',
      serviceId: services[0]?.id || '',
      recurrence: 'weekly',
      instances: [],
      isActive: true
    }
    updateStepData('scheduleTemplates', [...schedules, newSchedule])
    setExpandedSchedule(newSchedule.id)
  }

  const updateSchedule = (id: string, field: string, value: string | number | boolean | Record<string, unknown>[]) => {
    const updated = schedules.map(schedule =>
      schedule.id === id ? { ...schedule, [field]: value } : schedule
    )
    updateStepData('scheduleTemplates', updated)
  }

  const removeSchedule = (id: string) => {
    const updated = schedules.filter(schedule => schedule.id !== id)
    updateStepData('scheduleTemplates', updated)
  }

  const addInstance = (scheduleId: string) => {
    const schedule = schedules.find(s => s.id === scheduleId)
    if (schedule) {
      const newInstance = {
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '10:00'
      }
      updateSchedule(scheduleId, 'instances', [...schedule.instances, newInstance])
    }
  }

  const updateInstance = (scheduleId: string, index: number, field: string, value: string | number) => {
    const schedule = schedules.find(s => s.id === scheduleId)
    if (schedule) {
      const updatedInstances = [...schedule.instances]
      updatedInstances[index] = { ...updatedInstances[index], [field]: value }
      updateSchedule(scheduleId, 'instances', updatedInstances)
    }
  }

  const removeInstance = (scheduleId: string, index: number) => {
    const schedule = schedules.find(s => s.id === scheduleId)
    if (schedule) {
      const updatedInstances = schedule.instances.filter((_, i) => i !== index)
      updateSchedule(scheduleId, 'instances', updatedInstances)
    }
  }

  const loadTemplates = () => {
    const templates = scheduleTemplates[organizationProfile.businessType] || []
    const servicesMap = Object.fromEntries(
      services.filter(s => s.category === 'class').map((s, i) => [templates[i]?.serviceId || `service_${i}`, s.id])
    )
    
    const newSchedules = templates.map((template, index) => ({
      id: `schedule-${Date.now()}-${index}`,
      name: template.name,
      serviceId: servicesMap[template.serviceId] || services[0]?.id || '',
      recurrence: template.recurrence,
      instances: template.instances,
      isActive: true
    }))
    updateStepData('scheduleTemplates', newSchedules)
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  return (
    <div className="schedule-templates-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Schedule Templates
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Set up recurring schedules for your classes and programs. These templates will automatically create bookings.
      </p>

      {/* Info Box */}
      <div style={{
        padding: '1rem',
        backgroundColor: `${colors.primary}10`,
        border: `1px solid ${colors.primary}30`,
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <p style={{
          fontSize: '0.875rem',
          color: colors.text,
          margin: 0
        }}>
          💡 Schedule templates are optional. You can always add individual bookings manually or set up recurring schedules later.
        </p>
      </div>

      {/* Template Suggestion */}
      {schedules.length === 0 && organizationProfile.businessType !== 'other' && services.filter(s => s.category === 'class').length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}30`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.text,
            margin: 0
          }}>
            🗓️ Start with common schedules for {organizationProfile.businessType.replace(/_/g, ' ')}?
          </p>
          <button
            onClick={loadTemplates}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.813rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Use Templates
          </button>
        </div>
      )}

      {/* Schedules List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {schedules.map((schedule, index) => {
          const isExpanded = expandedSchedule === schedule.id
          const selectedService = services.find(s => s.id === schedule.serviceId)
          
          return (
            <div
              key={schedule.id}
              style={{
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              {/* Schedule Header */}
              <div
                style={{
                  padding: '1rem',
                  backgroundColor: colors.backgroundLight,
                  borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedSchedule(isExpanded ? null : schedule.id)}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CalendarDaysIcon style={{ 
                      width: '20px', 
                      height: '20px', 
                      color: colors.primary 
                    }} />
                    <div>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: colors.text,
                        margin: 0
                      }}>
                        {schedule.name || 'New Schedule'}
                      </h3>
                      {selectedService && (
                        <p style={{
                          fontSize: '0.813rem',
                          color: colors.textSecondary,
                          margin: 0,
                          marginTop: '0.125rem'
                        }}>
                          {selectedService.name} • {schedule.recurrence}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {schedule.instances.length > 0 && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: `${colors.primary}20`,
                        color: colors.primary,
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {schedule.instances.length} time{schedule.instances.length !== 1 ? 's' : ''} per week
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeSchedule(schedule.id)
                      }}
                      style={{
                        padding: '0.25rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: colors.error,
                        cursor: 'pointer'
                      }}
                    >
                      <TrashIcon style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Schedule Details (Expanded) */}
              {isExpanded && (
                <div style={{ padding: '1.5rem' }}>
                  <div style={{
                    display: 'grid',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    {/* Schedule Name */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.813rem',
                        fontWeight: '500',
                        marginBottom: '0.25rem',
                        color: colors.textSecondary
                      }}>
                        Schedule Name <span style={{ color: colors.error }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={schedule.name}
                        onChange={(e) => updateSchedule(schedule.id, 'name', e.target.value)}
                        placeholder="e.g., Morning Yoga Classes"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          backgroundColor: colors.backgroundLight,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>

                    {/* Service Selection */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Service/Class <span style={{ color: colors.error }}>*</span>
                        </label>
                        <select
                          value={schedule.serviceId}
                          onChange={(e) => updateSchedule(schedule.id, 'serviceId', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="">Select a service</option>
                          {services.filter(s => s.category === 'class').map(service => (
                            <option key={service.id} value={service.id}>
                              {service.name} ({service.duration} min)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Recurrence
                        </label>
                        <select
                          value={schedule.recurrence}
                          onChange={(e) => updateSchedule(schedule.id, 'recurrence', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        >
                          {recurrenceOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div>
                    <h4 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: colors.text,
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <ClockIcon style={{ width: '16px', height: '16px' }} />
                      Time Slots
                    </h4>

                    <div style={{
                      backgroundColor: colors.backgroundLight,
                      padding: '1rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`
                    }}>
                      {schedule.instances.map((instance, idx) => (
                        <div key={idx} style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr auto',
                          gap: '0.75rem',
                          alignItems: 'center',
                          marginBottom: idx < schedule.instances.length - 1 ? '0.75rem' : 0
                        }}>
                          <select
                            value={instance.dayOfWeek}
                            onChange={(e) => updateInstance(schedule.id, idx, 'dayOfWeek', parseInt(e.target.value))}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.813rem'
                            }}
                          >
                            {daysOfWeek.map(day => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </select>

                          <input
                            type="time"
                            value={instance.startTime}
                            onChange={(e) => updateInstance(schedule.id, idx, 'startTime', e.target.value)}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.813rem'
                            }}
                          />

                          <input
                            type="time"
                            value={instance.endTime}
                            onChange={(e) => updateInstance(schedule.id, idx, 'endTime', e.target.value)}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.813rem'
                            }}
                          />

                          <button
                            onClick={() => removeInstance(schedule.id, idx)}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: colors.error,
                              cursor: 'pointer'
                            }}
                          >
                            <TrashIcon style={{ width: '16px', height: '16px' }} />
                          </button>
                        </div>
                      ))}

                      <button
                        onClick={() => addInstance(schedule.id)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          backgroundColor: 'transparent',
                          border: `1px dashed ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.primary,
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem',
                          marginTop: schedule.instances.length > 0 ? '0.75rem' : '0'
                        }}
                      >
                        <PlusIcon style={{ width: '16px', height: '16px' }} />
                        Add Time Slot
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add Schedule Button */}
      <button
        onClick={addScheduleTemplate}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: 'transparent',
          border: `2px dashed ${colors.border}`,
          borderRadius: '8px',
          color: colors.primary,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primary
          e.currentTarget.style.backgroundColor = `${colors.primary}10`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <PlusIcon style={{ width: '20px', height: '20px' }} />
        Add Schedule Template
      </button>
    </div>
  )
}